<template>
  <div class="creare-page">
    <!-- Hero -->
    <div class="hero">
      <div class="hero-txt">
        <div class="hero-eyebrow">PORTAL HR</div>
        <h1 class="hero-title">Creare cerere</h1>
        <p class="hero-sub">Alege tipul cererii și completează formularul. Cererea intră în fluxul de aprobare.</p>
      </div>
      <v-icon class="hero-ic" size="46">mdi-file-plus-outline</v-icon>
    </div>

    <div class="grid">
      <!-- Selector tip: plăci -->
      <div class="card">
        <div class="card-head"><v-icon size="18" color="#ff5722" class="mr-2">mdi-format-list-checks</v-icon>Tip cerere</div>
        <div class="tip-list">
          <div v-if="loadingTipuri" class="tip-loading">
            <v-progress-circular indeterminate size="22" color="#ff5722" />
          </div>
          <button v-for="t in tipuri" :key="t.ID" type="button" class="tip-tile" :class="{ active: t.ID === tipSelectatId }" @click="selectTip(t.ID)">
            <span class="tile-ic" :style="{ background: metaFor(t.Cod).soft, color: metaFor(t.Cod).color }">
              <v-icon size="19">{{ metaFor(t.Cod).icon }}</v-icon>
            </span>
            <span class="tile-name">{{ t.Nume }}</span>
            <v-icon v-if="t.ID === tipSelectatId" size="18" color="#ff5722">mdi-check-circle</v-icon>
          </button>
        </div>
      </div>

      <!-- Formular dinamic -->
      <div class="card form-card">
        <div class="card-head">
          <template v-if="tipCurent">
            <span class="fh-ic" :style="{ background: kindMeta.soft, color: kindMeta.color }"><v-icon size="16">{{ kindMeta.icon }}</v-icon></span>
            {{ tipCurent.Nume }}
          </template>
          <template v-else><v-icon size="18" color="#ff5722" class="mr-2">mdi-pencil-outline</v-icon>Detalii cerere</template>
        </div>
        <div class="card-body">
          <div v-if="!tipCurent" class="empty">
            <v-icon size="44" color="#cbd5e1">mdi-gesture-tap-button</v-icon>
            <p>Alege un tip de cerere din stânga pentru a completa formularul.</p>
          </div>

          <div v-else-if="saved" class="succes">
            <div class="succes-badge"><v-icon size="40" color="#fff">mdi-check</v-icon></div>
            <h3>Cerere trimisă!</h3>
            <p v-if="ultimNr">Număr cerere: <strong>{{ ultimNr }}</strong></p>
            <v-btn class="mt-4 btn-save px-6" @click="resetForm"><v-icon start>mdi-plus</v-icon>Cerere nouă</v-btn>
          </div>

          <v-form v-else ref="formRef">
            <p class="form-desc">{{ kindMeta.desc }}</p>
            <div class="form-grid">
              <template v-if="cfg.range">
                <v-text-field v-model="f.dataStart" type="date" label="De la data" variant="outlined" density="comfortable" hide-details @update:model-value="calcZile" />
                <v-text-field v-model="f.dataSfarsit" type="date" label="Până la data (inclusiv)" variant="outlined" density="comfortable" hide-details @update:model-value="calcZile" />
              </template>

              <v-text-field v-if="cfg.singleDate" v-model="f.dataSingle" type="date" label="Data" variant="outlined" density="comfortable" hide-details />

              <div v-if="cfg.zileAuto" class="zile-auto g2">
                <v-icon size="18" color="#ff5722" class="mr-2">mdi-calendar-check</v-icon>
                Durată: <strong class="ml-1">{{ f.zileLucratoare }}</strong>&nbsp;zile lucrătoare
              </div>

              <template v-if="cfg.oraPlecare">
                <v-select v-model="f.oraH" :items="ore24" label="Ora plecării" variant="outlined" density="comfortable" hide-details />
                <v-select v-model="f.oraM" :items="minute60" label="Minute" variant="outlined" density="comfortable" hide-details />
              </template>

              <template v-if="cfg.durataHM">
                <v-select v-model="f.durH" :items="ore24" label="Durată (ore)" variant="outlined" density="comfortable" hide-details />
                <v-select v-model="f.durM" :items="minute60" label="Minute" variant="outlined" density="comfortable" hide-details />
              </template>

              <v-text-field v-if="cfg.durataOre" v-model="f.durataOre" type="number" label="Durată (ore)" variant="outlined" density="comfortable" hide-details />
              <v-text-field v-if="cfg.tara" v-model="f.tara" label="Țara" variant="outlined" density="comfortable" hide-details />
              <v-text-field v-if="cfg.localitate" v-model="f.localitate" label="Localitate" variant="outlined" density="comfortable" hide-details />

              <v-select v-if="cfg.inlocuitor" v-model="f.inlocuitor" :items="inlocuitori" item-title="Nume" item-value="Id"
                        label="Înlocuitor" variant="outlined" density="comfortable" clearable hide-details class="g2"
                        :hint="inlocuitori.length ? '' : 'Niciun coleg cu cont în departament'" />

              <v-textarea v-model="f.explicatie" label="Explicație" variant="outlined" density="comfortable" rows="3" hide-details class="g2" />
            </div>

            <v-alert v-if="eroare" type="error" variant="tonal" density="compact" class="mt-4">{{ eroare }}</v-alert>

            <v-btn block size="large" class="btn-save mt-4" :loading="saving" @click="submit">
              <v-icon start>mdi-send</v-icon>Trimite cererea
            </v-btn>
          </v-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import PORTAL_Tichete from '@/services/Portal/PORTAL_Tichete'
import PORTAL_CereriConcedii from '@/services/Portal/PORTAL_CereriConcedii'

const PROP = { Date: -2852, Explanation: -2853, Duration: -2854, Substitute: -2855, LeavingTime: -2856, EndDate: -2858, Destination: -2860, DurationDays: -2864 }

function ddmmyyyy(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}

export default defineComponent({
  name: 'CreareCerere',
  data() {
    return {
      tipuri: [] as any[],
      loadingTipuri: true,
      tipSelectatId: null as number | null,
      inlocuitori: [] as any[],
      saving: false,
      saved: false,
      eroare: '',
      ultimNr: '',
      ore24: Array.from({ length: 24 }, (_, i) => i),
      minute60: Array.from({ length: 60 }, (_, i) => i),
      f: {
        dataStart: '', dataSfarsit: '', dataSingle: '',
        oraH: 8, oraM: 0, durH: 0, durM: 0, durataOre: '',
        zileLucratoare: 0, tara: '', localitate: '', inlocuitor: null as any, explicatie: '',
      },
    }
  },
  computed: {
    tipCurent(): any { return this.tipuri.find(t => t.ID === this.tipSelectatId) || null; },
    kind(): string {
      const cod = (this.tipCurent?.Cod || '').toUpperCase();
      if (cod === 'MDA') return 'wfh';
      if (cod === 'TTRA') return 'training';
      if (cod === 'TOSP') return 'overtime';
      if (cod === 'TINV') return 'invoire';
      if (cod.startsWith('TDEL')) return 'deplasare';
      return 'generic';
    },
    extern(): boolean { return (this.tipCurent?.Cod || '').toUpperCase().includes('-E'); },
    cfg(): any {
      switch (this.kind) {
        case 'wfh': return { range: true, zileAuto: true, explicatie: true };
        case 'deplasare': return { range: true, zileAuto: true, tara: this.extern, localitate: true, inlocuitor: true, explicatie: true };
        case 'training': return { range: true, durataOre: true, localitate: true, inlocuitor: true, explicatie: true };
        case 'overtime': return { singleDate: true, durataHM: true, explicatie: true };
        case 'invoire': return { singleDate: true, oraPlecare: true, durataHM: true, inlocuitor: true, explicatie: true };
        default: return { explicatie: true };
      }
    },
    kindMeta(): any { return this.metaFor(this.tipCurent?.Cod ?? ''); },
  },
  async mounted() {
    await Promise.all([this.incarcaTipuri(), this.incarcaInlocuitori()]);
  },
  methods: {
    async incarcaTipuri() {
      this.loadingTipuri = true;
      try { this.tipuri = (await PORTAL_Tichete.getTipuri()).data ?? []; }
      catch { this.tipuri = []; }
      finally { this.loadingTipuri = false; }
    },
    async incarcaInlocuitori() {
      try { this.inlocuitori = (await PORTAL_CereriConcedii.getFormInit()).data?.Inlocuitori ?? []; }
      catch { this.inlocuitori = []; }
    },
    onTipChange() { this.saved = false; this.eroare = ''; },
    selectTip(id: number) { this.tipSelectatId = id; this.onTipChange(); },
    metaFor(cod: string): any {
      const c = (cod || '').toUpperCase();
      let kind = 'generic';
      if (c === 'MDA') kind = 'wfh';
      else if (c === 'TTRA') kind = 'training';
      else if (c === 'TOSP') kind = 'overtime';
      else if (c === 'TINV') kind = 'invoire';
      else if (c.startsWith('TDEL')) kind = 'deplasare';
      const map: Record<string, any> = {
        wfh: { icon: 'mdi-home-account', color: '#0ea5e9', soft: '#eff6ff', desc: 'Muncă de acasă pe un interval de zile.' },
        deplasare: { icon: 'mdi-car', color: '#8b5cf6', soft: '#f5f3ff', desc: 'Ordin de deplasare / delegație.' },
        training: { icon: 'mdi-school', color: '#f59e0b', soft: '#fff7ed', desc: 'Participare la training / instruire.' },
        overtime: { icon: 'mdi-clock-plus-outline', color: '#ef4444', soft: '#fef2f2', desc: 'Ore suplimentare într-o zi.' },
        invoire: { icon: 'mdi-exit-run', color: '#16a34a', soft: '#ecfdf5', desc: 'Învoire pe ore într-o zi.' },
        generic: { icon: 'mdi-file-document-outline', color: '#64748b', soft: '#f1f5f9', desc: 'Cerere HR.' },
      };
      return map[kind];
    },
    async calcZile() {
      if (!this.cfg.zileAuto || !this.f.dataStart || !this.f.dataSfarsit) return;
      try { this.f.zileLucratoare = (await PORTAL_Tichete.getZileLucratoare(this.f.dataStart, this.f.dataSfarsit)).data ?? 0; }
      catch { /* ignore */ }
    },
    resetForm() {
      this.saved = false; this.eroare = ''; this.tipSelectatId = null;
      this.f = { dataStart: '', dataSfarsit: '', dataSingle: '', oraH: 8, oraM: 0, durH: 0, durM: 0, durataOre: '', zileLucratoare: 0, tara: '', localitate: '', inlocuitor: null, explicatie: '' };
    },
    valideaza(): string {
      const f = this.f, c = this.cfg;
      if (c.range && (!f.dataStart || !f.dataSfarsit)) return 'Selectează intervalul de date.';
      if (c.range && f.dataSfarsit < f.dataStart) return 'Data de sfârșit trebuie să fie după data de început.';
      if (c.singleDate && !f.dataSingle) return 'Selectează data.';
      if (c.durataOre) {
        const n = Number(f.durataOre);
        if (!f.durataOre || isNaN(n) || n < 1) return 'Durata trebuie să fie un număr mai mare ca 0.';
      }
      if (this.kind === 'training' && !f.localitate) return 'Localitatea este obligatorie.';
      return '';
    },
    buildProps(): { DictionarID: number; Valoare: string }[] {
      const f = this.f, props: { DictionarID: number; Valoare: string }[] = [];
      const push = (d: number, v: any) => props.push({ DictionarID: d, Valoare: v == null ? '' : String(v) });
      const durataHM = () => (f.durH + f.durM / 60);

      switch (this.kind) {
        case 'wfh':
          push(PROP.Date, ddmmyyyy(f.dataStart)); push(PROP.EndDate, ddmmyyyy(f.dataSfarsit));
          push(PROP.DurationDays, f.zileLucratoare); push(PROP.Explanation, f.explicatie);
          break;
        case 'deplasare':
          push(PROP.Date, ddmmyyyy(f.dataStart)); push(PROP.EndDate, ddmmyyyy(f.dataSfarsit));
          push(PROP.DurationDays, f.zileLucratoare);
          push(PROP.Destination, this.extern ? `${f.tara} - ${f.localitate}`.trim() : f.localitate);
          push(PROP.Substitute, f.inlocuitor); push(PROP.Explanation, f.explicatie);
          break;
        case 'training':
          push(PROP.Date, ddmmyyyy(f.dataStart)); push(PROP.EndDate, ddmmyyyy(f.dataSfarsit));
          push(PROP.Duration, f.durataOre); push(PROP.Destination, f.localitate);
          push(PROP.Substitute, f.inlocuitor); push(PROP.Explanation, f.explicatie);
          break;
        case 'overtime':
          push(PROP.Date, ddmmyyyy(f.dataSingle)); push(PROP.Duration, durataHM()); push(PROP.Explanation, f.explicatie);
          break;
        case 'invoire':
          push(PROP.Date, ddmmyyyy(f.dataSingle)); push(PROP.Explanation, f.explicatie);
          push(PROP.LeavingTime, `${f.oraH}:${String(f.oraM).padStart(2, '0')}`);
          push(PROP.Duration, durataHM()); push(PROP.Substitute, f.inlocuitor);
          break;
        default:
          push(PROP.Explanation, f.explicatie);
      }
      return props;
    },
    async submit() {
      this.eroare = this.valideaza();
      if (this.eroare) return;
      this.saving = true;
      try {
        const resp = await PORTAL_Tichete.creeaza({ TipID: this.tipSelectatId!, Proprietati: this.buildProps() });
        this.ultimNr = resp.data?.TicketNr ?? '';
        this.saved = true;
      } catch (e: any) {
        this.eroare = e?.response?.data ?? 'Eroare la salvarea cererii.';
      } finally {
        this.saving = false;
      }
    },
  },
})
</script>

<style scoped>
.creare-page { padding: 16px; width: 100%; }
.hero {
  display: flex; align-items: center; justify-content: space-between;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 24px 28px; color: #fff; box-shadow: 0 10px 30px rgba(255,87,34,.28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-ic { opacity: .5; }

.grid { display: grid; grid-template-columns: 320px 1fr; gap: 18px; align-items: start; }
@media (max-width: 860px) { .grid { grid-template-columns: 1fr; } }

.card { background: #fff; border: 1px solid #eef1f6; border-radius: 16px; box-shadow: 0 1px 3px rgba(15,23,42,.05); overflow: hidden; }
.card-head { display: flex; align-items: center; gap: 9px; padding: 15px 18px; border-bottom: 1px solid #f1f5f9; font-size: 13px; font-weight: 800; color: #1e293b; }
.fh-ic { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 8px; }
.card-body { padding: 18px; }

/* Plăci tip cerere */
.tip-list { padding: 10px; display: flex; flex-direction: column; gap: 6px; }
.tip-loading { display: flex; justify-content: center; padding: 24px; }
.tip-tile { display: flex; align-items: center; gap: 12px; width: 100%; text-align: left; background: #fff; border: 1px solid #eef1f6; border-radius: 12px; padding: 11px 13px; cursor: pointer; transition: all .14s; font: inherit; }
.tip-tile:hover { border-color: #ffd0bd; background: #fffaf7; }
.tip-tile.active { border-color: #ff5722; background: #fff5f0; box-shadow: 0 2px 10px rgba(255,87,34,.12); }
.tile-ic { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; }
.tile-name { flex: 1; font-size: 13px; font-weight: 700; color: #1e293b; line-height: 1.25; }

/* Stări */
.empty { text-align: center; color: #94a3b8; padding: 56px 20px; }
.empty p { margin-top: 12px; font-size: 14px; max-width: 300px; margin-left: auto; margin-right: auto; }
.succes { text-align: center; padding: 44px 10px; }
.succes-badge { width: 72px; height: 72px; border-radius: 50%; background: linear-gradient(140deg,#16a34a,#4ade80); display: flex; align-items: center; justify-content: center; margin: 0 auto; box-shadow: 0 8px 22px rgba(22,163,74,.3); }
.succes h3 { color: #16a34a; font-weight: 800; margin-top: 16px; font-size: 18px; }
.succes p { color: #64748b; margin-top: 6px; }

/* Formular */
.form-desc { font-size: 12.5px; color: #94a3b8; margin: 0 0 16px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 14px; }
.form-grid .g2 { grid-column: 1 / -1; }
@media (max-width: 560px) { .form-grid { grid-template-columns: 1fr; } .form-grid .g2 { grid-column: auto; } }
.zile-auto { display: flex; align-items: center; background: #fff1ec; border: 1px solid #ffe0d1; border-radius: 10px; padding: 11px 14px; font-size: 13px; color: #b23c17; }
.btn-save { background: #ff5722 !important; color: #fff !important; font-weight: 700; text-transform: none; letter-spacing: 0; border-radius: 12px; }
</style>
